import axios, { AxiosInstance } from 'axios'
import type {
  User,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  Conversation,
  Message,
} from '@/types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle token refresh on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refresh_token')
        if (!refreshToken) {
          throw new Error('No refresh token')
        }

        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        })

        const { access } = response.data
        localStorage.setItem('access_token', access)

        originalRequest.headers.Authorization = `Bearer ${access}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Auth service
export const authService = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/login/', data)
    return response.data
  },

  register: async (data: RegisterRequest): Promise<User> => {
    const response = await apiClient.post<User>('/auth/register/', data)
    return response.data
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/users/me/')
    return response.data
  },
}

// Conversation service
export const conversationService = {
  list: async (): Promise<Conversation[]> => {
    const response = await apiClient.get('/conversations/')
    // Handle both paginated and non-paginated responses
    if (Array.isArray(response.data)) {
      return response.data
    } else if (response.data && response.data.results) {
      return response.data.results
    }
    return []
  },

  get: async (id: number): Promise<Conversation> => {
    const response = await apiClient.get<Conversation>(`/conversations/${id}/`)
    return response.data
  },

  create: async (): Promise<Conversation> => {
    const response = await apiClient.post<Conversation>('/conversations/', {})
    return response.data
  },

  close: async (id: number): Promise<void> => {
    await apiClient.post(`/conversations/${id}/close/`)
  },

  requestSpecialist: async (id: number, specialistId: number): Promise<any> => {
    const response = await apiClient.post(`/conversations/${id}/request-specialist/`, {
      specialist_id: specialistId,
    })
    return response.data
  },

  getSpecialistRecommendations: async (conversationId: number): Promise<any> => {
    const response = await apiClient.get(`/conversations/${conversationId}/specialist-recommendations/`)
    return response.data
  },
}

// Message service
export const messageService = {
  list: async (conversationId: number): Promise<Message[]> => {
    const response = await apiClient.get(`/conversations/${conversationId}/messages/`)
    // Handle both paginated and non-paginated responses
    if (Array.isArray(response.data)) {
      return response.data
    } else if (response.data && response.data.results) {
      return response.data.results
    }
    return []
  },

  send: async (conversationId: number, content: string): Promise<Message> => {
    const response = await apiClient.post<Message>(
      `/conversations/${conversationId}/messages/`,
      { content }
    )
    return response.data
  },

  markRead: async (messageId: number): Promise<void> => {
    await apiClient.post(`/messages/${messageId}/mark_read/`)
  },

  getAIResponse: async (conversationId: number): Promise<any> => {
    const response = await apiClient.post(`/conversations/${conversationId}/ai-response/`)
    return response.data
  },

  getSpecialistRecommendations: async (conversationId: number): Promise<any> => {
    const response = await apiClient.get(`/conversations/${conversationId}/specialist-recommendations/`)
    return response.data
  },
}

// Specialist service
export const specialistService = {
  getProfile: async (): Promise<any> => {
    const response = await apiClient.get('/specialists/me/')
    return response.data
  },

  updateProfile: async (data: any): Promise<any> => {
    const response = await apiClient.patch('/specialists/me/', data)
    return response.data
  },

  getConversations: async (filters?: { status?: string; unread?: boolean }): Promise<any> => {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.unread !== undefined) params.append('unread', String(filters.unread))

    const response = await apiClient.get(`/specialists/me/conversations/?${params.toString()}`)
    return response.data
  },

  getNotifications: async (unreadOnly?: boolean): Promise<any> => {
    const params = unreadOnly ? '?unread=true' : ''
    const response = await apiClient.get(`/specialists/me/notifications/${params}`)
    return response.data
  },

  markNotificationRead: async (notificationId: number): Promise<void> => {
    await apiClient.post(`/specialists/me/notifications/${notificationId}/read/`)
  },

  takeoverConversation: async (conversationId: number): Promise<any> => {
    const response = await apiClient.post(`/conversations/${conversationId}/takeover/`)
    return response.data
  },

  completeConversation: async (conversationId: number): Promise<any> => {
    const response = await apiClient.post(`/conversations/${conversationId}/complete/`)
    return response.data
  },
}

export default apiClient
